import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  CreateScheduleOneOffInput,
  ListScheduleTemplatesInput,
  MoveScheduleOccurrenceInput,
  ScheduleRepository,
  StartedScheduleWorkout,
  SwapScheduleOccurrenceInput,
} from '../../application/ports/schedule.repository';
import type {ActiveScheduleWorkout} from '../../domain/active-schedule-workout';
import {
  InvalidScheduleInputError,
  NetworkError,
  ScheduleConflictError,
  ScheduleNotFoundError,
  ScheduleRejectedError,
  ServerError,
  UnauthorizedError,
  type ScheduleError,
} from '../../domain/errors';
import type {ScheduleCalendar, ScheduleCalendarRange} from '../../domain/schedule-calendar';
import type {ScheduleOccurrence} from '../../domain/schedule-occurrence';
import type {ScheduleOneOffId} from '../../domain/schedule-one-off-id.value-object';
import type {ScheduleTemplateOption} from '../../domain/schedule-template-option';
import type {SaveScheduleWeekEntry, ScheduleWeekSlot} from '../../domain/schedule-week-slot';
import type {ScheduleCalendarDtoToScheduleCalendarMapper} from '../mappers/schedule-calendar-dto-to-schedule-calendar.mapper';
import type {ScheduleWeekSlotDtoToScheduleWeekSlotMapper} from '../mappers/schedule-week-slot-dto-to-schedule-week-slot.mapper';
import type {WorkoutDtoToActiveScheduleWorkoutMapper} from '../mappers/workout-dto-to-active-schedule-workout.mapper';
import type {WorkoutTemplateSummaryDtoToScheduleTemplateOptionMapper} from '../mappers/workout-template-summary-dto-to-schedule-template-option.mapper';

/**
 * `/v1/schedule` over the typed Hono client, plus the two endpoints the calendar
 * reads from other slices (`/workout-templates` for the picker and `/workouts/active`
 * for the in-progress banner). Nothing outside this adapter knows those transport shapes.
 */
export class RpcScheduleRepository implements ScheduleRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly calendarMapper: ScheduleCalendarDtoToScheduleCalendarMapper,
    private readonly weekSlotMapper: ScheduleWeekSlotDtoToScheduleWeekSlotMapper,
    private readonly templateOptionMapper: WorkoutTemplateSummaryDtoToScheduleTemplateOptionMapper,
    private readonly activeWorkoutMapper: WorkoutDtoToActiveScheduleWorkoutMapper,
  ) {}

  async getCalendar(
    range: ScheduleCalendarRange | null,
  ): Promise<Result<ScheduleCalendar, ScheduleError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.schedule.calendar.$get({
          query: range === null ? {} : {from: range.from, to: range.to},
        }),
      );
      return ok(this.calendarMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getWeek(): Promise<Result<readonly ScheduleWeekSlot[], ScheduleError>> {
    try {
      const dto = await parseResponse(this.rpcClient.schedule.week.$get());
      return ok(dto.map(slot => this.weekSlotMapper.transform(slot)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async saveWeek(entries: readonly SaveScheduleWeekEntry[]): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(
        this.rpcClient.schedule.week.$put({
          json: {entries: [...entries]},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async createOneOff(input: CreateScheduleOneOffInput): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(
        this.rpcClient.schedule['one-offs'].$post({
          json: {date: input.date, templateId: input.templateId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async removeOneOff(oneOffId: ScheduleOneOffId): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(
        this.rpcClient.schedule['one-offs'][':oneOffId'].$delete({
          param: {oneOffId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async skipOccurrence(occurrence: ScheduleOccurrence): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(this.rpcClient.schedule.occurrences.skip.$post({json: occurrence}));
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async restoreOccurrence(occurrence: ScheduleOccurrence): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(this.rpcClient.schedule.occurrences.restore.$post({json: occurrence}));
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async moveOccurrence(input: MoveScheduleOccurrenceInput): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(
        this.rpcClient.schedule.occurrences.move.$post({
          json: {occurrence: input.occurrence, moveToDate: input.moveToDate},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async swapOccurrence(input: SwapScheduleOccurrenceInput): Promise<Result<void, ScheduleError>> {
    try {
      await parseResponse(
        this.rpcClient.schedule.occurrences.swap.$post({
          json: {occurrence: input.occurrence, templateId: input.templateId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async startOccurrence(
    occurrence: ScheduleOccurrence,
  ): Promise<Result<StartedScheduleWorkout, ScheduleError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.schedule.occurrences.start.$post({json: occurrence}),
      );
      return ok({workoutId: dto.workoutId});
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async listTemplates(
    input: ListScheduleTemplatesInput,
  ): Promise<Result<Page<ScheduleTemplateOption>, ScheduleError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['workout-templates'].$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.q.length > 0 && {q: input.q}),
          },
        }),
      );

      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }

      return ok({
        items: dto.data.map(item => this.templateOptionMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getActiveWorkout(): Promise<Result<ActiveScheduleWorkout | null, ScheduleError>> {
    try {
      const dto = await parseResponse(this.rpcClient.workouts.active.$get());
      if (dto === null || dto === undefined) {
        return ok(null);
      }

      return ok(this.activeWorkoutMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  private mapError(cause: unknown): Result<never, ScheduleError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new ScheduleNotFoundError());
      }
      if (cause.statusCode === 409) {
        return err(new ScheduleConflictError());
      }
      if (cause.statusCode === 422) {
        return err(new ScheduleRejectedError());
      }
      if (cause.statusCode === 400) {
        return err(new InvalidScheduleInputError());
      }
      return err(new ServerError());
    }

    return err(new NetworkError());
  }
}
